import React, { Suspense, lazy } from 'react';
import { Route, Switch, BrowserRouter, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';
import './App.css';
import './views/ShoppingDiagnostics/SDForm/SDLegs/SDLegs.css';
import AuthComponent from './auth/AuthComponent';
import BaseLayout from './layout/BaseLayout';
import applicationStore from './store/ApplicationStore';
import notificationStore from './store/NotificationStore';
import NotificationsManager from './views/NotificationsManager/NotificationsManager';
import LoadingBlock from './components/LoadingBlock/LoadingBlock';
import PrivateRouter from './components/PrivateRouter/PrivateRouter';
import Login from './views/Pages/Login/Login';
import { DEFAULT_PATHS } from './utils/ApiUtils';
import { CATEGORIES } from './utils/PermissionsChecker';
import { ThemeProvider } from './layout/ThemeProvider';
import { ThemeConfigKeys } from './utils/Constants';
import BookingTolerances from './views/BookingTolerances/BookingTolerances';

const Home = lazy(() => import('./views/Pages/Home/Home'));
const Page404 = lazy(() => import('./views/Pages/Page404/Page404'));
const Page403 = lazy(() => import('./views/Pages/Page403/Page403'));
const SearchesManager = lazy(() => import('./views/SearchesManager/SearchesManager'));
const SearchManagerRule = lazy(() => import('./views/SearchesManager/SearchManagerRule/SearchManagerRule'));
const SearchesManagerV2 = lazy(() => import('./views/SearchesManagerV2/SearchesManagerV2'));
const SearchesManagerV2Editor = lazy(() => import('./views/SearchesManagerV2/SearchesManagerV2Editor'));
const BookingCreateManagerV2 = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2'));
const BookingCreateManagerV2Editor = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2Editor'));
const BookingCreateManagerV2RemarksEditor = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2RemarksEditor'));
const BookingCreateManagerV2SSREditor = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2SSREditor'));
const BookingTolerancesEditor = lazy(() => import('./views/BookingTolerances/BookingTolerancesEditor'));
const Diagnostics = lazy(() => import('./views/ShoppingDiagnostics/Diagnostics'));
const BookingCreateManager = lazy(() => import('./views/BCM/OSIS/BookingCreateManagerOSIS'));
const BCMRule = lazy(() => import('./views/BCM/OSIS/BCMRuleOSIS/BCMRuleOSIS'));
const BCMRemarksRule = lazy(() => import('./views/BCM/Remarks/BCMRemarksRule/BCMRemarksRule'));
const BookingCreateManagerRemarks = lazy(() => import('./views/BCM/Remarks/BookingCreateManagerRemarks'));
const BookingCreateManagerSSR = lazy(() => import('./views/BCM/SSR/BookingCreateManagerSSR'));
const BookingCreateManagerSSREditor = lazy(() => import('./views/BCM/SSR/BookingCreateManagerSSREditor'));
const Regions = lazy(() => import('./views/BusinessData/Regions/Regions'));
const BusinessData = lazy(() => import('./views/BusinessData/BusinessData'));
const CommercialControls = lazy(() => import('./views/BusinessData/CommercialControls/CommercialControls'));
const RegionsEditor = lazy(() => import('./views/BusinessData/Regions/RegionsEditor/RegionsEditor'));
const Countries = lazy(() => import('./views/BusinessData/Countries/Countries'));
const CountriesEditor = lazy(() => import('./views/BusinessData/Countries/CountriesEditor/CountriesEditor'));
const Locations = lazy(() => import('./views/BusinessData/Locations/Locations'));
const LocationsEditor = lazy(() => import('./views/BusinessData/Locations/LocationsEditor/LocationsEditor'));
const Stations = lazy(() => import('./views/BusinessData/Stations/Stations'));
const StationsEditor = lazy(() => import('./views/BusinessData/Stations/StationsEditor/StationsEditor'));
const Airlines = lazy(() => import('./views/BusinessData/Airlines/Airlines'));
const AirlinesEditor = lazy(() => import('./views/BusinessData/Airlines/AirlinesEditor/AirlinesEditor'));
const Brands = lazy(() => import('./views/BusinessData/Brands/Brands'));
const BrandsEditor = lazy(() => import('./views/BusinessData/Brands/BrandsEditor/BrandsEditor'));
const Markets = lazy(() => import('./views/BusinessData/Markets/Markets'));
const MarketsEditor = lazy(() => import('./views/BusinessData/Markets/MarketsEditor/MarketsEditor'));
const DistributionChannels = lazy(() => import('./views/BusinessData/DistributionChannels/DistributionChannels'));
const DistributionChannelsEditor = lazy(() => import('./views/BusinessData/DistributionChannels/DistributionChannelsEditor/DistributionChannelsEditor'));
const Agencies = lazy(() => import('./views/BusinessData/Agencies/Agencies'));
const AgenciesEditor = lazy(() => import('./views/BusinessData/Agencies/AgenciesEditor/AgenciesEditor'));
const AgencyGroups = lazy(() => import('./views/BusinessData/AgencyGroups/AgencyGroups'));
const AgencyGroupsEditor = lazy(() => import('./views/BusinessData/AgencyGroups/AgencyGroupsEditor/AgencyGroupsEditor'));
const Markups = lazy(() => import('./views/Markups/Markups'));
const MarkupsV2 = lazy(() => import('./views/MarkupsV2/MarkupsV2'));
const MarkupsEditor = lazy(() => import('./views/Markups/MarkupsEditor/MarkupsEditor'));
const MarkupsV2Editor = lazy(() => import('./views/MarkupsV2/MarkupsV2Editor'));
const ProviderPreference = lazy(() => import('./views/ProviderPreference/ProviderPreference'));
const ProviderPreferenceEditor = lazy(() => import('./views/ProviderPreference/ProviderPreferenceEditor'));
const ProviderCredentialsV2 = lazy(() => import('./views/ProviderCredentialsV2/ProviderCredentialsV2'));
const ProviderCredentialsV2Editor = lazy(() => import('./views/ProviderCredentialsV2/ProviderCredentialsV2Editor'));
const IdentityProviderSettings = lazy(() => import('./views/IdentityProviderSettings/IdentityProviderSettings'));
const IdentityProviderSettingsEditor = lazy(() => import('./views/IdentityProviderSettings/IdentityProviderSettingsEditor/IdentityProviderSettingsEditor'));
const IdentitySettings = lazy(() => import('./views/IdentitySettings/IdentitySettings'));
const IdentitySettingsEditor = lazy(() => import('./views/IdentitySettings/IdentitySettingsEditor/IdentitySettingsEditor'));
const TicketTLO = lazy(() => import('./views/TicketTLO/TicketTLO'));
const TicketTLOEditor = lazy(() => import('./views/TicketTLO/TicketTLOEditor/TicketTLOEditor'));
const TicketTLOV2 = lazy(() => import('./views/TicketTLOV2/TicketTLOV2'));
const TicketTLOV2Editor = lazy(() => import('./views/TicketTLOV2/TicketTLOV2Editor'));
const UserPermissions = lazy(() => import('./views/UserPermissions/UserPermissions'));
const UserPermissionsEditor = lazy(() => import('./views/UserPermissions/UserPermissionsEditor/UserPermissionsEditor'));
const GlobalFiltersV2 = lazy(() => import('./views/GlobalFiltersV2/GlobalFiltersV2'));
const GlobalFiltersV2Editor = lazy(() => import('./views/GlobalFiltersV2/GlobalFiltersV2Editor'));
const ActivityLogV2 = lazy(() => import('./views/ActivityLogV2/ActivityLogV2'));
const Supplier = lazy(() => import('./views/SupplierManager/Supplier'));
const SupplierEditor = lazy(() => import('./views/SupplierManager/SupplierEditor'));
const AncillariesMapping = lazy(() => import('./views/AncillariesMapping/AncillariesMapping'));
const AncillaryDistribution = lazy(() => import('./views/AncillaryDistribution/AncillaryDistribution'));
const Ancillaries = lazy(() => import('./views/Ancillaries/Ancillaries'));
const AncillariesManager = lazy(() => import('./views/AncillariesManager/AncillariesManager'));
const AncillariesManagerEditor = lazy(() => import('./views/AncillariesManager/AncillariesManagerEditor'));
const AncillariesMappingEditor = lazy(() => import('./views/AncillariesMapping/AncillariesMappingEditor'));
const AncillaryDistributionEditor = lazy(() => import('./views/AncillaryDistribution/AncillaryDistributionEditor'));
const VccCredentialsV2 = lazy(() => import('./views/VccCredentialsV2/VccCredentialsV2'));
const VccCredentialsV2Editor = lazy(() => import('./views/VccCredentialsV2/VccCredentialsV2Editor'));
const MasterDataSetup = lazy(() => import('./views/MasterDataSetup/MasterDataSetup'));
function App() {
  return (
    <>
      <BrowserRouter>
        <ThemeProvider themeKey={ThemeConfigKeys.DefaultTheme}>
          <Provider store={applicationStore}>
            <AuthComponent>
              <Switch>
                <BaseLayout>
                  <Suspense fallback={<LoadingBlock />}>
                    <Switch>
                      <Route path="/" exact component={Login} />
                      <Route path={`/home`} exact component={Home} />
                      <PrivateRouter
                        category={CATEGORIES.SEARCHES_MANAGER}
                        path={`/${DEFAULT_PATHS.SEARCHES_MANAGER}/rule/:id`}
                        exact
                        component={SearchesManagerV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.SEARCHES_MANAGER}
                        path={`/${DEFAULT_PATHS.SEARCHES_MANAGER}/rule`}
                        exact
                        component={SearchesManagerV2Editor}
                      />
                      <PrivateRouter category={CATEGORIES.SEARCHES_MANAGER}
                        path={`/${DEFAULT_PATHS.SEARCHES_MANAGER}`}
                        exact component={SearchesManagerV2} />
                      <PrivateRouter
                        category={CATEGORIES.ADMIN_PORTAL}
                        path={`/${DEFAULT_PATHS.DIAGNOSTICS}`}
                        exact
                        component={Diagnostics}
                      />
                      <PrivateRouter
                        category={CATEGORIES.SHOPPING_DIAGNOSTICS}
                        path={`/${DEFAULT_PATHS.SHOPPING_DIAGNOSTICS}`}
                        exact
                        render={() => <Redirect to={`/${DEFAULT_PATHS.DIAGNOSTICS}`} />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.SHOPPING_DIAGNOSTICS}
                        path={`/${DEFAULT_PATHS.SHOPPING_DIAGNOSTICS_V2}`}
                        exact
                        render={() => <Redirect to={`/${DEFAULT_PATHS.DIAGNOSTICS}`} />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_REMARKS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/remarks/rule/:id`}
                        exact
                        component={BookingCreateManagerV2RemarksEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_REMARKS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/remarks/rule/`}
                        exact
                        component={BookingCreateManagerV2RemarksEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_REMARKS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/remarks/`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'remarks' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_SSR}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/ssr/`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'ssr' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_SSR}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/ssr/rule/`}
                        exact
                        component={BookingCreateManagerV2SSREditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_SSR}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/ssr/rule/:id`}
                        exact
                        component={BookingCreateManagerV2SSREditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/osi/rule/:id`}
                        exact
                        component={BookingCreateManagerV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/osi/rule`}
                        exact
                        component={BookingCreateManagerV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/osi`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'osi' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'osi' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter category={CATEGORIES.USER_PERMISSIONS} requireWriteAccess path={`/${DEFAULT_PATHS.USER_PERMISSIONS}`} exact component={UserPermissions} />
                      <PrivateRouter
                        category={CATEGORIES.USER_PERMISSIONS}
                        requireWriteAccess
                        path={`/${DEFAULT_PATHS.USER_PERMISSIONS}/edit/:id`}
                        exact
                        component={UserPermissionsEditor}
                      />
                      <PrivateRouter category={CATEGORIES.REGIONS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/regions/edit/:id`} exact component={RegionsEditor} />
                      <PrivateRouter category={CATEGORIES.REGIONS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/regions/new`} exact component={RegionsEditor} />
                      <Route path={`/${DEFAULT_PATHS.BUSINESS_DATA}`} exact component={BusinessData} />
                      <PrivateRouter category={CATEGORIES.REGIONS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/regions`} exact render={(props) => <BusinessData><Regions {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.COUNTRIES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/countries/edit/:id`}
                        exact
                        component={CountriesEditor}
                      />
                      <PrivateRouter category={CATEGORIES.COUNTRIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/countries/new`} exact component={CountriesEditor} />
                      <PrivateRouter category={CATEGORIES.COUNTRIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/countries`} exact render={(props) => <BusinessData><Countries {...props} /></BusinessData>} />
                      <PrivateRouter category={CATEGORIES.CITIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/cities/edit/:id`} exact component={LocationsEditor} />
                      <PrivateRouter category={CATEGORIES.CITIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/cities/new`} exact component={LocationsEditor} />
                      <PrivateRouter category={CATEGORIES.CITIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/cities`} exact render={(props) => <BusinessData><Locations {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.AIRPORTS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/stations/edit/:id`}
                        exact
                        component={StationsEditor}
                      />
                      <PrivateRouter category={CATEGORIES.AIRPORTS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/stations/new`} exact component={StationsEditor} />
                      <PrivateRouter category={CATEGORIES.AIRPORTS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/stations`} exact render={(props) => <BusinessData><Stations {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.AIRLINES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/airlines/edit/:id`}
                        exact
                        component={AirlinesEditor}
                      />
                      <PrivateRouter category={CATEGORIES.AIRLINES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/airlines/new`} exact component={AirlinesEditor} />
                      <PrivateRouter category={CATEGORIES.AIRLINES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/airlines`} exact render={(props) => <BusinessData><Airlines {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/agencies/edit/:id`}
                        exact
                        component={AgenciesEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/agencies/new`}
                        exact
                        component={AgenciesEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/agencies`}
                        exact
                        render={(props) => <CommercialControls><Agencies {...props} /></CommercialControls>}
                      />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/agency-groups/edit/:id`}
                        exact
                        component={AgencyGroupsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/agency-groups/new`}
                        exact
                        component={AgencyGroupsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/agency-groups`}
                        exact
                        render={(props) => <CommercialControls><AgencyGroups {...props} /></CommercialControls>}
                      />
                      <PrivateRouter category={CATEGORIES.MARKUPS_V2} path={`/${DEFAULT_PATHS.MARKUPS}`} exact component={MarkupsV2} />
                      <PrivateRouter category={CATEGORIES.MARKUPS_V2} path={`/${DEFAULT_PATHS.MARKUPS}/rule`} exact component={MarkupsV2Editor} />
                      <PrivateRouter category={CATEGORIES.MARKUPS_V2} path={`/${DEFAULT_PATHS.MARKUPS}/rule/:id`} exact component={MarkupsV2Editor} />

                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_PREFERENCE}
                        path={`/${DEFAULT_PATHS.PROVIDER_PREFERENCE}/new`}
                        exact
                        component={ProviderPreferenceEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_PREFERENCE}
                        path={`/${DEFAULT_PATHS.PROVIDER_PREFERENCE}/edit/:id`}
                        exact
                        component={ProviderPreferenceEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_PREFERENCE}
                        path={`/${DEFAULT_PATHS.PROVIDER_PREFERENCE}`}
                        exact
                        component={ProviderPreference}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.PROVIDER_CREDENTIALS}/rule/:id`}
                        exact
                        component={ProviderCredentialsV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.PROVIDER_CREDENTIALS}/rule`}
                        exact
                        component={ProviderCredentialsV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.PROVIDER_CREDENTIALS}`}
                        exact
                        component={ProviderCredentialsV2}
                      />

                      <PrivateRouter category={CATEGORIES.TICKET_TLO} path={`/${DEFAULT_PATHS.TICKET_TLO}/rule/:id`} exact component={TicketTLOV2Editor} />
                      <PrivateRouter category={CATEGORIES.TICKET_TLO} path={`/${DEFAULT_PATHS.TICKET_TLO}/rule`} exact component={TicketTLOV2Editor} />
                      <PrivateRouter category={CATEGORIES.TICKET_TLO} path={`/${DEFAULT_PATHS.TICKET_TLO}`} exact component={TicketTLOV2} />

                      <PrivateRouter category={CATEGORIES.GLOBAL_FILTERS} path={`/${DEFAULT_PATHS.GLOBAL_FILTERS}/rule/:id`} exact component={GlobalFiltersV2Editor} />
                      <PrivateRouter category={CATEGORIES.GLOBAL_FILTERS} path={`/${DEFAULT_PATHS.GLOBAL_FILTERS}/rule`} exact component={GlobalFiltersV2Editor} />
                      <PrivateRouter category={CATEGORIES.GLOBAL_FILTERS} path={`/${DEFAULT_PATHS.GLOBAL_FILTERS}`} exact component={GlobalFiltersV2} />

                      <Route path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}`} exact component={CommercialControls} />
                      <PrivateRouter
                        category={CATEGORIES.BRANDS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/brands/edit/:id`}
                        exact
                        component={BrandsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BRANDS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/brands/new`}
                        exact
                        component={BrandsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BRANDS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/brands`}
                        exact
                        render={(props) => <CommercialControls><Brands {...props} /></CommercialControls>}
                      />
                      <PrivateRouter
                        category={CATEGORIES.MARKETS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/markets/edit/:id`}
                        exact
                        component={MarketsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.MARKETS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/markets/new`}
                        exact
                        component={MarketsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.MARKETS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/markets`}
                        exact
                        render={(props) => <CommercialControls><Markets {...props} /></CommercialControls>}
                      />
                      <PrivateRouter
                        category={CATEGORIES.DISTRIBUTION_CHANNELS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/distribution-channels/edit/:id`}
                        exact
                        component={DistributionChannelsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.DISTRIBUTION_CHANNELS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/distribution-channels/new`}
                        exact
                        component={DistributionChannelsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.DISTRIBUTION_CHANNELS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/${DEFAULT_PATHS.COMMERCIAL_CONTROLS}/distribution-channels`}
                        exact
                        render={(props) => <CommercialControls><DistributionChannels {...props} /></CommercialControls>}
                      />

                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS}/rule/:id`}
                        exact
                        component={IdentityProviderSettingsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS}/rule`}
                        exact
                        component={IdentityProviderSettingsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS}/`}
                        exact
                        component={IdentityProviderSettings}
                      />
                      <PrivateRouter category={CATEGORIES.ACTIVITY_LOG} path={`/${DEFAULT_PATHS.ACTIVITY_LOG}`} exact component={ActivityLogV2} />
                      <PrivateRouter
                        category={CATEGORIES.TECHNICAL_LOGS}
                        path={`/${DEFAULT_PATHS.TECHNICAL_LOGS}`}
                        exact
                        render={() => <Redirect to={`/${DEFAULT_PATHS.DIAGNOSTICS}?tab=technical-logs`} />}
                      />
                      <PrivateRouter category={CATEGORIES.IDENTITY_SETTINGS} path={`/${DEFAULT_PATHS.IDENTITY_SETTINGS}/rule/:id`} exact component={IdentitySettingsEditor} />
                      <PrivateRouter category={CATEGORIES.IDENTITY_SETTINGS} path={`/${DEFAULT_PATHS.IDENTITY_SETTINGS}/rule`} exact component={IdentitySettingsEditor} />
                      <PrivateRouter category={CATEGORIES.IDENTITY_SETTINGS} path={`/${DEFAULT_PATHS.IDENTITY_SETTINGS}/`} exact component={IdentitySettings} />
                      <PrivateRouter category={CATEGORIES.PROVIDERS} path={`/${DEFAULT_PATHS.PROVIDERS}`} exact component={Supplier} />
                      <PrivateRouter category={CATEGORIES.PROVIDERS} path={`/${DEFAULT_PATHS.PROVIDERS}/rule/:id`} exact component={SupplierEditor} />
                      <PrivateRouter category={CATEGORIES.PROVIDERS} path={`/${DEFAULT_PATHS.PROVIDERS}/rule`} exact component={SupplierEditor} />                      
                      <Route path="/ancillaries" exact component={Ancillaries} />
                      <PrivateRouter category={CATEGORIES.ANCILLARY_MANAGER} path="/ancillaries/manager" exact render={(props) => <Ancillaries><AncillariesManager {...props} /></Ancillaries>} />
                      <PrivateRouter category={CATEGORIES.ANCILLARY_SUPPLIER} path="/ancillaries/mapping" exact render={(props) => <Ancillaries><AncillariesMapping {...props} /></Ancillaries>} />
                      <PrivateRouter category={CATEGORIES.ANCILLARY_DISTRIBUTION} path="/ancillaries/distribution" exact render={(props) => <Ancillaries><AncillaryDistribution {...props} /></Ancillaries>} />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_MANAGER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_MANAGER}`}
                        exact
                        render={() => <Redirect to="/ancillaries/manager" />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_MANAGER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_MANAGER}/new`}
                        exact
                        component={AncillariesManagerEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_MANAGER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_MANAGER}/edit/:id`}
                        exact
                        component={AncillariesManagerEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_SUPPLIER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_SUPPLIER}`}
                        exact
                        render={() => <Redirect to="/ancillaries/mapping" />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_SUPPLIER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_SUPPLIER}/edit/:id`}
                        exact
                        component={AncillariesMappingEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_SUPPLIER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_SUPPLIER}/new`}
                        exact
                        component={AncillariesMappingEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_DISTRIBUTION}
                        path={`/${DEFAULT_PATHS.ANCILLARY_DISTRIBUTION}`}
                        exact
                        render={() => <Redirect to="/ancillaries/distribution" />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_DISTRIBUTION}
                        path={`/${DEFAULT_PATHS.ANCILLARY_DISTRIBUTION}/edit/:id`}
                        exact
                        component={AncillaryDistributionEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_DISTRIBUTION}
                        path={`/${DEFAULT_PATHS.ANCILLARY_DISTRIBUTION}/new`}
                        exact
                        component={AncillaryDistributionEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.MASTER_DATA_SETUP}/`}
                        exact
                        component={MasterDataSetup}
                      />
                      <PrivateRouter
                        category={CATEGORIES.VCC_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.VCC_CREDENTIALS}/rule/:id`}
                        exact
                        component={VccCredentialsV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.VCC_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.VCC_CREDENTIALS}/rule`}
                        exact
                        component={VccCredentialsV2Editor}
                      />
                      <PrivateRouter category={CATEGORIES.VCC_CREDENTIALS} path={`/${DEFAULT_PATHS.VCC_CREDENTIALS}`} exact component={VccCredentialsV2} />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_TOLERANCES}
                        path={`/${DEFAULT_PATHS.BOOKING_TOLERANCES}`}
                        exact
                        component={BookingTolerances}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_TOLERANCES}
                        path={`/${DEFAULT_PATHS.BOOKING_TOLERANCES}/rule/:id`}
                        exact
                        component={BookingTolerancesEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_TOLERANCES}
                        path={`/${DEFAULT_PATHS.BOOKING_TOLERANCES}/rule`}
                        exact
                        component={BookingTolerancesEditor}
                      />
                      <Route path={`/403`} exact component={Page403} />
                      <Route component={Page404} />
                    </Switch>
                  </Suspense>
                </BaseLayout>
              </Switch>
            </AuthComponent>
          </Provider>
          <Provider store={notificationStore}>
            <NotificationsManager />
          </Provider>
        </ThemeProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
